import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { CallCenterIntrabank } from 'src/front/api/apiInstance';
import {
  CallCenterIntrabankMoneyTransferDetailsResponse,
  CallCenterIntrabankMoneyTransferHistoryResponseData,
  ReferenceDataType,
} from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import { IntrabankTransferHistorySchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components//Datepicker';
import {
  Form,
  FormElement,
  FormRow,
  InfoColumn,
  InfoLabel,
  InfoRow,
  Separator,
} from 'src/front/components/FormStyles';
import { TABLE_WIDTHS } from 'src/front/constant';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import { useAccountState } from 'src/front/contexts/TransferHistoryContext';
import styled from 'styled-components';
import { CallCenterTableWrapper } from './AccountSelectionFormIntrabank';

const CornerFormElement = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 15px;
`;
const tableHeaders = [
  'general_documentNumber_label',
  'general_components_transactionDate_text',
  'general_components_loanAccountNumber_text',
  'general_comment_label',
  'general_components_debitTurnover_text',
  'general_components_loanAmount_text',
  'general_components_reducedValue_text',
];

const TransferDetailsTableHeaders = [
  'general_components_account_text',
  'general_components_accountName_text',
  'general_currency_label',
  'general_components_type_label',
  'general_components_currencyAmount_text',
  'general_components_amount_text',
];

export interface HistoryFormData {
  beginDate: Date | null;
  endDate: Date | null;
  product: Option | null;
  cardType: Option | null;
}
const defaultValues = {
  beginDate: null,
  endDate: null,
  product: null,
  cardType: null,
};
const HistoryFormIntrabank: FC = () => {
  const { accountValues } = useAccountState();
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasNextPageTransferDetails, setHasNextPageTransferDetails] = useState<boolean>(false);
  const [historyList, setHistoryList] = useState<
    CallCenterIntrabankMoneyTransferHistoryResponseData[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [historyPageTransferDetails, setHistoryPageTransferDetails] = useState<number>(1);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [showTransferDetails, setShowTransferDetails] = useState<boolean>(false);
  const [selectedMoneyTransferDetails, setSelectedMoneyTransferDetails] = useState<
    CallCenterIntrabankMoneyTransferDetailsResponse
  >();
  const [products, setProducts] = useState<Option[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<TableRow | undefined>(
    accountValues?.selectedAccount || undefined
  );

  const cardTypes = usePRM(ReferenceDataType.ACCOUNTTYPE);
  const resolver = useYupResolver(IntrabankTransferHistorySchema, { todayMaxControl: true });
  const { watch, register, errors, getValues, setValue, handleSubmit, formState } = useForm<
    HistoryFormData
  >({
    defaultValues: {
      ...defaultValues,
      beginDate: processDate || null,
      endDate: processDate || null,
      product: null,
    },
    resolver,
  });

  useEffect(() => {
    (async () => {
      const { data } = await CallCenterIntrabank.getProductComboIntrabank();
      setProducts(data.parameterDataList.map(optionFormat));
    })();
  }, []);

  useEffect(() => {
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
    register({ name: 'product' });
    register({ name: 'cardType' });
  }, [register]);

  useEffect(() => {
    const findDebit = cardTypes.find((item) => item.label === 'DEBIT');
    setValue('cardType', findDebit || null);
  }, [cardTypes, setValue]);

  const loadHistoryList = useCallback(
    async (pageNumber = historyPage, forceRefresh = false) => {
      setSelectedAccount(undefined);
      setShowTransferDetails(false);
      setHistoryLoading(true);
      const { beginDate, endDate, product } = getValues();
      const { data } = await CallCenterIntrabank.getMoneyTransferHistoryIntrabank({
        pageNumber,
        refresh: !pageNumber || forceRefresh,
        accountNo: accountValues?.accountNumber || '',
        endDate: formatDate(endDate),
        startDate: formatDate(beginDate),
        accountType: dictionaryLineFormat(watch('cardType')),
        product: dictionaryLineFormat(product),
        rowCountPerPage: 20,
      });

      if (data.result) {
        setHistoryList(data.transferList);
        setHasNextPage(data.hasNextPage);
        setHistoryPage(pageNumber || 1);
      }
      setHistoryLoading(false);
    },
    [accountValues, getValues, historyPage, watch]
  );
  const handleDateChange = (fieldName: string) => (date: Date): void =>
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });
  const onSearchSubmit = (): void => {
    loadHistoryList(1, true);
    setShowResult(true);
  };

  const handleProductChange = (selectedProduct: string) => (option: Option | null) => {
    setValue(selectedProduct, option);
  };

  const handleTypeChange = (cardType: Option | null): void => {
    setValue('cardType', cardType);
  };

  const onAccountSelect = useCallback(
    async (item?: TableRow, pageNumber = historyPageTransferDetails) => {
      const selected = historyList.find((account) => {
        return account.slip === item?.id;
      });
      setHistoryLoading(true);
      const { data } = await CallCenterIntrabank.getMoneyTransferDetailsIntrabank({
        pageNumber,
        refresh: true,
        slip: item?.id || '',
        referenceNumber: selected?.referenceNumber || '',
        rowCountPerPage: 5,
      });

      if (data.result) {
        setSelectedMoneyTransferDetails(data);
        setHasNextPageTransferDetails(data.hasNextPage);
        setHistoryPageTransferDetails(pageNumber || 1);
        setShowTransferDetails(true);
      }
      setSelectedAccount(item);
      setHistoryLoading(false);
    },
    [historyList, historyPageTransferDetails]
  );

  const transferList: TableRow[] = historyList.map((transfer, index) => {
    return {
      id: transfer.slip,
      columns: [
        transfer.documentNumber,
        transfer.transactionDate,
        transfer.loanAccountNumber,
        transfer.comment,
        transfer.debitTurnover,
        transfer.loanAmount,
        transfer.reducedValue,
      ],
    };
  });

  const transferDetailsList: TableRow[] =
    selectedMoneyTransferDetails?.detailTransferList.map((item, index) => ({
      id: index.toString(),
      columns: [
        item.account,
        item.accountName,
        item.currency,
        item.type,
        item.currencyAmount,
        item.amount,
      ],
    })) || [];
  return (
    <>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_components_accountNumber_text" />:
        </InfoLabel>
        {accountValues?.accountNumber}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_currency_label" />:
        </InfoLabel>
        {accountValues?.currency}
      </InfoRow>
      <Separator />
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.beginDate?.message as string)}
              inputLabel={`${t('general_components_startDate_label')} *`}
              onChange={handleDateChange('beginDate')}
              value={watch('beginDate') as Date}
            />
          </FormElement>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.endDate?.message as string)}
              inputLabel={`${t('general_components_endDate_label')} *`}
              onChange={handleDateChange('endDate')}
              value={watch('endDate') as Date}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t(errors?.cardType?.message as string)}
              items={cardTypes}
              label={t('general_components_type_label')}
              name="cardType"
              onChange={handleTypeChange}
              value={watch('cardType')}
              required
            />
          </FormElement>
          <FormElement>
            <Combobox
              errorMessage={t(errors?.product?.message as string)}
              items={products}
              label={t('general_components_product_text')}
              name="product"
              onChange={handleProductChange('product')}
              value={watch('product')}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <FormElement />
          <CornerFormElement>
            <Button
              color={ButtonColor.Primary}
              disabled={formState.isSubmitting}
              size={ButtonSize.Medium}
              text={t('general_components_search_button')}
              type="submit"
            />
          </CornerFormElement>
        </FormRow>
      </form>
      {showResult ? (
        <CallCenterTableWrapper>
          <Table
            emptyTableText={t('general_components_notFound_errorMessage')}
            hasNextPage={hasNextPage}
            headers={tableHeaders.map((head) => t(head))}
            items={transferList}
            loading={historyLoading}
            loadPageItems={loadHistoryList}
            onSelect={onAccountSelect}
            pageNumber={historyPage}
            selectedItem={selectedAccount}
            tableWidth={TABLE_WIDTHS.DESKTOP_2X}
            widths={['10', '10', '20', '25', '10', '10', '10']}
            clearItemOnPageChange
            withRadio
          />
        </CallCenterTableWrapper>
      ) : null}

      {showTransferDetails ? (
        <>
          <Separator />
          <Separator />
          <InfoRow>
            <InfoColumn>
              <InfoLabel>
                <Trans i18nKey="general_components_product_text" />:
              </InfoLabel>
              {selectedMoneyTransferDetails?.product}
            </InfoColumn>
            <InfoColumn>
              <InfoLabel>
                <Trans i18nKey="general_components_applicationDate_text" />:
              </InfoLabel>
              {selectedMoneyTransferDetails?.applicationDate}
            </InfoColumn>
          </InfoRow>
          <InfoRow>
            <InfoColumn>
              <InfoLabel>
                <Trans i18nKey="general_components_productOperation_text" />:
              </InfoLabel>
              {selectedMoneyTransferDetails?.productOperation}
            </InfoColumn>
            <InfoColumn>
              <InfoLabel>
                <Trans i18nKey="general_components_transactionDate_text" />:
              </InfoLabel>
              {selectedMoneyTransferDetails?.transactionDate}
            </InfoColumn>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <Trans i18nKey="general_components_channel_text" />:
            </InfoLabel>
            {selectedMoneyTransferDetails?.channel}
          </InfoRow>
          <Separator />
          <Form>
            <CallCenterTableWrapper>
              <Table
                emptyTableText={t('general_components_notFound_errorMessage')}
                hasNextPage={hasNextPageTransferDetails}
                headers={TransferDetailsTableHeaders.map((head) => t(head))}
                items={transferDetailsList}
                loading={historyLoading}
                pageNumber={historyPageTransferDetails}
                tableWidth={TABLE_WIDTHS.DESKTOP_2X}
                widths={['10', '10', '10', '10', '10', '10']}
                clearItemOnPageChange
              />
            </CallCenterTableWrapper>
          </Form>
        </>
      ) : null}
    </>
  );
};
export default HistoryFormIntrabank;
